import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  CardHeader,
  CardContent,
  Grid,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import CardLayout from "../../layouts/CardLayout";
import { PASSWORDS_REGEX } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import { adminServices } from "../../services/AllServices";
import { Helmet } from "react-helmet-async";

const ChangeAdminPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required("New Password is required")
    .matches(PASSWORDS_REGEX, "Password must be at least 8 characters, include one uppercase, one lowercase, one number, and one special character"),
  cpassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

export default function ChangeAdminPassword() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      cpassword: "",
    },
    validationSchema: ChangeAdminPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await adminServices.changePassword({
          id,
          newPassword: values.password,
          confirmPassword: values.cpassword,
        });
        
        if (response.status === 200) {
          toast.success(response?.data?.message || "Password changed successfully");
          navigate("/admin-management");
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit } = formik;

  return (
    <Box>
      <Helmet>
        <title>Change Admin Password</title>
      </Helmet>
      <CardLayout>
        <CardHeader title="Change Admin Password" subheader="Set a new password for the administrator" />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="New Password"
                    required
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    required
                    type={showConfirmPassword ? "text" : "password"}
                    name="cpassword"
                    value={values.cpassword}
                    onChange={handleChange}
                    error={Boolean(touched.cpassword && errors.cpassword)}
                    helperText={touched.cpassword && errors.cpassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      Update Password
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}
